import cx from "classnames";
import { ErrorMessage, Field, Form, Formik, FormikBag, FormikHelpers } from "formik";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as Yup from 'yup';
import Checkbox from "../../common/components/Forms/Checkbox/Checkbox";
import AFAddressSelector from "../components/AFAddressSelector";
import AFLayout from "../components/AFLayout";
import AFNavigation from "../components/AFNavigation";
import { ContactTimeWindowOptions } from "../constants/SetupData";
import { PageType } from "../enums/PageType";
import { useAFForm } from "../hooks/useAFForm";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import { IViewContext, ViewContext } from "../providers/ViewStateProvider";

type AFParams = {
    applicant: string;
};

type ContactDetailsForm = {
    email: string;
    phone: string;
    bestContactTime: string;
    addressLine1: string;
    addressCity: string;
    addressPostcode: string;
    postalAddressLine1: string;
    postalAddressCity: string;
    postalAddressPostcode: string;
};

const AFContactDetailsPage: React.FC = () => {
    const applicantRef = useParams<AFParams>().applicant;
    const { actions, selectors } = useContext(AppFormControllerContext);
    const { setError, isLoading } = useContext<IViewContext>(ViewContext);
    const [hidePostalAddress, setHidePostalAddress] = useState(true);

    // Ensure we have the applicant info available, and that the current page state is set.
    const applicantInfo = selectors.getCurrentApplicant(applicantRef);

    const filterFormField = useCallback((fieldName: string) => {
        if (fieldName == "hasDifferentPostalAddress") return false;

        return true;
    }, []);

    const {
        loadFormPage,
        getCurrentPage,
        updateApplicant,
        navigateForward,
        navigateBackward,
        handleFieldChange,
        updateMultipleFields,
        saveFormChanges
    } = useAFForm({ pageType: PageType.Contact, currentClientNumber: applicantInfo?.clientNumber ?? 1, filterFormField: filterFormField});


    const [formModel, setFormModel] = useState<ContactDetailsForm>({  // Default data that will be overridden when loading from the server.
        email: applicantInfo?.email ?? '',
        phone: applicantInfo?.phone ?? '',
        bestContactTime: applicantInfo?.bestContactTime ?? 'Anytime',
        addressLine1: '',
        addressCity: '',
        addressPostcode: '',
        postalAddressLine1: '',
        postalAddressCity: '',
        postalAddressPostcode: ''
    });


    // Form navigation

    const handleSubmit = useCallback((values: ContactDetailsForm, { setSubmitting }: FormikHelpers<ContactDetailsForm>): void => {
        saveFormChanges(() => {     // Saved successfully
                const updatedApplicant = {
                    ...applicantInfo,
                    email: values.email,
                    phone: values.phone,
                    bestContactTime: values.bestContactTime
                };

                updateApplicant(updatedApplicant);
                setSubmitting(false);
                navigateForward(updatedApplicant);
            },
            () => {     // Bypassed - error or nothing to save
                setSubmitting(false);
            });
    }, [applicantInfo, navigateForward]);

    const handleBack = useCallback((e): void => {
        e.preventDefault();
        if (isLoading()) return;

        navigateBackward(applicantInfo);
    }, [applicantInfo, navigateBackward]);


    // Initial load (when the current applicant changes)

    const updateLoadedFields = useCallback((responses: any): void => {
        if (responses.postalAddressLine1 && responses.postalAddressLine1 != responses.addressLine1) {
            setHidePostalAddress(false);
        }
        setFormModel({ ...formModel, ...responses });
    }, []);

    const resetForm = useCallback((): void => {
        setFormModel({
            email: '',
            phone: '',
            bestContactTime: 'Anytime',
            addressLine1: '',
            addressCity: '',
            addressPostcode: '',
            postalAddressLine1: '',
            postalAddressCity: '',
            postalAddressPostcode: ''
        });
    }, []);

    useEffect(() => {
        loadFormPage(updateLoadedFields, resetForm);
    }, [applicantRef, loadFormPage]);


    //validation

    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .email('Please enter a valid email address')
            .required('Email is required'),
        phone: Yup.string()
            .matches(/^[0-9+\-\(\)\s]{7,20}$/, 'Please enter a valid phone number')
            .required('Phone number is required'),
        bestContactTime: Yup.string()
            .required('Best contact time is required')
    });


    // Other component events

    const EditPostalAddress = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        var isSameAddress = e.target.checked;
        setHidePostalAddress(isSameAddress);

        if (isSameAddress) {
            var updatedFields = {
                postalAddressLine1: formModel.addressLine1,
                postalAddressCity: formModel.addressCity,
                postalAddressPostcode: formModel.addressPostcode
            };
            setFormModel({ ...formModel, ...updatedFields });
            updateMultipleFields(updatedFields);
            saveFormChanges(null, null);
        }
    }, [formModel]);



    /*abTesting.convertAll(20, "", getProductTypeAsString, 0);
    abTesting.convertAll(18, "", getProductTypeAsString, 0);*/

    return (
        <AFLayout currentPage={getCurrentPage()}>
            <Formik
                initialValues={formModel}
                validationSchema={validationSchema}
                enableReinitialize={true}
                onSubmit={handleSubmit}
                validateOnBlur={true}
                validateOnChange={true}
                validateOnMount={true}>
                {({ errors, touched, isValid, isSubmitting, handleBlur }) => {
                    const onBlur = (e) => {
                        handleBlur(e);
                        saveFormChanges(null, null);
                    };
                    return (
                        <Form noValidate onChange={handleFieldChange}>
                            <div className={cx("panel-std-white", "layout-panel-content", "gap-post-block")}>
                                <h1>Contact Details</h1>
                                <h3>{applicantInfo.firstName}</h3>
                                <p>
                                    Thanks {applicantInfo.firstName}. If you have trouble throughout this process, or have any questions,
                                    our team are here to help.
                                    Enter your contact details below in case we need to get hold of you.
                                </p>

                                <div className="form-field">
                                    <label htmlFor="email">Email address</label>
                                    <Field id="email" name="email" type="email" onBlur={onBlur} className={touched.email && errors.email ? 'error' : ''} />
                                    <ErrorMessage name="email" component="div" className="text-error" />
                                </div>

                                <div className="form-field">
                                    <label>Phone number</label>
                                    <Field id="phone" name="phone" type="phone" onBlur={onBlur} className={touched.phone && errors.phone ? 'error' : ''} />
                                    <ErrorMessage name="phone" component="div" className="text-error" />
                                </div>

                                <div className="form-field">
                                    <label>Best time to be contacted</label>
                                    <Field id="bestContactTime" name="bestContactTime" as="select" onBlur={onBlur} className={touched.bestContactTime && errors.bestContactTime ? 'error' : ''}>
                                        {ContactTimeWindowOptions.map((option, i) => (
                                            <option value={option.value} key={`option-${i}`}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Field>
                                    <ErrorMessage name="bestContactTime" component="div" className="text-error" />
                                </div>

                                {applicantInfo.clientNumber === 1 &&
                                    <div className="form-field">
                                        <label>Home address</label>
                                        <AFAddressSelector
                                            placeholder="Start typing your address..."
                                            addressField="addressLine1" cityField="addressCity" postcodeField="addressPostcode"
                                            onAddressChanged={updateMultipleFields} />
                                        <Checkbox
                                            className="space-l gap-pre-m"
                                            checked={hidePostalAddress}
                                            name="hasDifferentPostalAddress"
                                            size="l"
                                            label="This is also my postal address"
                                                isDisabled={isSubmitting}
                                                onChange={EditPostalAddress} />
                                    </div>
                                }

                                {(!hidePostalAddress &&
                                    <div className="form-field">
                                        <label>Postal address</label>
                                        <AFAddressSelector
                                            placeholder="Start typing your address..."
                                            addressField="postalAddressLine1" cityField="postalAddressCity" postcodeField="postalAddressPostcode"
                                            onAddressChanged={updateMultipleFields} />
                                    </div>
                                )}



                            </div>
                            <AFNavigation
                                handleBack={handleBack}
                                skipIsVisible={false}
                                backIsVisible={true}
                                forwardIsVisible={true}
                                forwardIsDisabled={!isValid || isSubmitting}
                                forwardType="submit"
                                forwardText="Next"
                            />
                        </Form>
                    );
                }}
            </Formik>
        </AFLayout>
    );
};

export default AFContactDetailsPage;
